import React from "react";
import { Formik } from "formik";
import * as Yup from "yup";

import { Form, NumberInputField } from "@jsluna/form";
import { Link } from "@jsluna/link";
import { ProgressSpinner } from "@jsluna/progress";
import {
  Notification,
  Card,
  SubmitButton,
  BLACK_BOX_ELEMENT_ID,
  trackEvent,
} from "@jsainsburyplc/id-ui-common";

import { categoryTypes, actionTypes } from "constants/tracking";
import {
  OTP_FORM_ID,
  OTP,
  requiredMessage,
  minLengthMessage,
} from "constants/mfaAndOtp";
import { missionNames } from "constants/missions";
import { missionId } from "utils/urls";
import { isIovationEnabled } from "utils/env";
import { withMetaData } from "selectors";
import { isHidingLinks } from "utils/hideLinks";

const initialValues = {
  [OTP]: "",
};

const codeLength = 6;

const FORM_SCHEMA = Yup.object({
  [OTP]: Yup.string()
    .min(codeLength, minLengthMessage(codeLength))
    .required(requiredMessage),
});

const trackRequestForHelp = () => {
  trackEvent({
    category: categoryTypes.MFA,
    action: actionTypes.CLICK,
    label: `${missionId}: No longer user this mobile number?`,
  });
};

const isIovationEnabledEnv = isIovationEnabled();

const OtpForm = ({
  onRequestCode,
  onSubmit,
  isRequestingCode,
  notification,
  hideTroubleMessage,
}) => {
  const metadata = withMetaData();
  const hideLinks = isHidingLinks();
  return (
    <>
      {notification && (
        <div className="ln-u-margin-bottom*2">
          <Notification type={notification.type}>
            {notification.text}
          </Notification>
        </div>
      )}

      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={FORM_SCHEMA}
        validateOnMount={true}
      >
        {({
          isValid,
          handleSubmit,
          isSubmitting,
          errors,
          touched,
          handleBlur,
          getFieldProps,
        }) => {
          return (
            <Card>
              <Form onSubmit={handleSubmit} id={OTP_FORM_ID} method="post">
                <NumberInputField
                  {...getFieldProps(OTP)}
                  data-testid="OTP_FIELD"
                  label="Enter your code"
                  error={touched[OTP] && errors[OTP]}
                  required
                  maxLength={codeLength}
                  onBlur={handleBlur}
                  placeholder={`Enter your ${codeLength} digit code`}
                  autoComplete="one-time-code"
                  aria-describedby="codeValidation"
                  className="ln-u-margin-bottom*2"
                />
                <span>This code is valid for 10 minutes.</span>
                {isIovationEnabledEnv && (
                  <input
                    data-testid={BLACK_BOX_ELEMENT_ID}
                    type="hidden"
                    name={BLACK_BOX_ELEMENT_ID}
                    id={BLACK_BOX_ELEMENT_ID}
                  />
                )}
                <div className="ln-u-margin-top*3">
                  <SubmitButton
                    data-testid="submit-code"
                    submitHandler={handleSubmit}
                    label="Continue"
                    fullWidth={true}
                    isLoading={isSubmitting}
                    isLoadingLabel="Continue"
                    disabled={isSubmitting || !isValid}
                  />
                </div>
              </Form>
            </Card>
          );
        }}
      </Formik>

      <div className="ln-u-padding-top*4">
        <span>Not received your code?</span>
      </div>
      <div>
        {isRequestingCode ? (
          <ProgressSpinner
            data-testid="spinner"
            size="icon"
            className="ln-u-margin-left"
          />
        ) : (
          <Link
            cta
            bare
            color="alpha"
            data-testid="request-code"
            href="#"
            onClick={onRequestCode}
          >
            Send me a new code
          </Link>
        )}
      </div>

      {!hideTroubleMessage && (
        <>
          {missionId === missionNames.NECTAR && !hideLinks && (
            <div className="ln-u-padding-top*2">
              <span>
                If you're still having trouble, please contact{" "}
                <Link
                  cta
                  bare
                  color="alpha"
                  data-testid="contact-support"
                  target="_blank"
                  href={metadata.live_chat_url}
                  onClick={trackRequestForHelp}
                >
                  Nectar Help
                </Link>
              </span>
            </div>
          )}

          {missionId === missionNames.GOL && (
            <div className="ln-u-padding-top*4">
              <span>
                If you're still having trouble, please contact us on{" "}
                <Link
                  cta
                  bare
                  color="alpha"
                  data-testid="contact-support"
                  href={`tel:${metadata.call_centre_number}`}
                  onClick={trackRequestForHelp}
                >
                  {metadata.call_centre_number}
                </Link>
              </span>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default OtpForm;
